import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getStockReturns, removeStockReturn, updateStockReturn } from '../../redux/actions/stockReturn/stockReturnAction';
import { fetchEmployees } from '../../redux/actions/employee/employeeAction';
import DataTable from 'react-data-table-component';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import { confirmDelete, showSuccess } from '../../utils/alertUtils';
import { toast } from 'react-toastify';

const StockReturnTable = ({ currentUser, onDelete }) => {
  const dispatch = useDispatch();
  const { stockReturns, loading, error } = useSelector((state) => state.stockReturn);
  const { employees } = useSelector((state) => state.employee);
  const { user } = useSelector((state) => state.login);

  // State for filters
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [stockCodeFilter, setStockCodeFilter] = useState('');

  // State for filter modal
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({});

  // State for edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingReturn, setEditingReturn] = useState(null);
  const [editFormData, setEditFormData] = useState({
    processed_by: '',
    status: 'processed'
  });

  useEffect(() => {
    dispatch(getStockReturns());
    dispatch(fetchEmployees());
  }, [dispatch]);

  // Calculate status counts
  const statusCounts = useMemo(() => {
    return stockReturns.reduce((acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    }, {});
  }, [stockReturns]);

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    let filtered = stockReturns;

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item =>
        (item.stock_code && item.stock_code.toLowerCase().includes(searchLower)) ||
        (item.stock_name && item.stock_name.toLowerCase().includes(searchLower)) ||
        (item.emp_code && item.emp_code.toLowerCase().includes(searchLower)) ||
        (item.return_reason && item.return_reason.toLowerCase().includes(searchLower))
      );
    }

    if (fromDate) {
      filtered = filtered.filter(item => new Date(item.created_at) >= new Date(fromDate));
    }

    if (toDate) {
      filtered = filtered.filter(item => new Date(item.created_at) <= new Date(toDate + 'T23:59:59'));
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    if (selectedEmployee) {
      filtered = filtered.filter(item => item.emp_code === selectedEmployee.emp_code);
    }

    if (stockCodeFilter) {
      filtered = filtered.filter(item => 
        item.stock_code && item.stock_code.toLowerCase().includes(stockCodeFilter.toLowerCase())
      );
    }

    return filtered;
  }, [stockReturns, search, fromDate, toDate, statusFilter, selectedEmployee, stockCodeFilter]);

  const handleClearFilters = () => {
    setSearch('');
    setFromDate('');
    setToDate('');
    setStatusFilter('all');
    setSelectedEmployee(null);
    setStockCodeFilter('');
  };

  const handleApplyFilters = () => {
    setFromDate(tempFilters.fromDate || '');
    setToDate(tempFilters.toDate || '');
    setSelectedEmployee(tempFilters.selectedEmployee || null);
    setStockCodeFilter(tempFilters.stockCodeFilter || '');
    setFilterModalOpen(false);
  };

  const handleOpenFilterModal = () => {
    setTempFilters({
      fromDate,
      toDate,
      selectedEmployee,
      stockCodeFilter
    });
    setFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setFilterModalOpen(false);
  };

  const handleExport = () => {
    const csvRows = [
      ['Stock Code', 'Stock Name', 'Employee Code', 'Quantity', 'Weight', 'Return Reason', 'Status', 'Created At', 'Processed By', 'Processed At'],
      ...filteredData.map(row => [
        row.stock_code || '',
        row.stock_name || '',
        row.emp_code || '',
        row.qty || '',
        row.weight || '',
        row.return_reason || '',
        row.status || '',
        row.created_at ? new Date(row.created_at).toLocaleString() : '',
        row.processed_by || '',
        row.processed_at ? new Date(row.processed_at).toLocaleString() : ''
      ])
    ];
    const csvContent = csvRows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stock_returns.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete('Are you sure?', 'This stock return record will be deleted permanently!');
    if (result.isConfirmed) {
      try {
        await dispatch(removeStockReturn(id));
        showSuccess('Deleted!', 'The stock return record has been deleted.');
        if (onDelete) onDelete();
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete stock return record';
        toast.error(errorMessage);
      }
    }
  };

  const handleEdit = async (id) => {
    const returnRecord = stockReturns.find(item => item.id === id);
    if (returnRecord) {
      setEditingReturn(returnRecord);
      setEditFormData({
        processed_by: returnRecord.processed_by || currentUser?.username || '',
        status: returnRecord.status || 'processed'
      });
      setEditModalOpen(true);
    }
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
    setEditingReturn(null);
    setEditFormData({
      processed_by: '',
      status: 'processed'
    });
  };

  const handleEditSave = async () => {
    if (!editingReturn) return;

    try {
      const updateData = {
        ...editFormData,
        processed_at: editFormData.status === 'processed' ? new Date().toISOString() : null
      };

      await dispatch(updateStockReturn(editingReturn.id, updateData));
      toast.success('Stock return updated successfully!');
      handleEditCancel();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update stock return';
      toast.error(errorMessage);
    }
  };

  const columns = [
    { name: 'Stock Code', selector: (row) => row.stock_code, sortable: true, width: '160px' },
    { name: 'Stock Name', selector: (row) => row.stock_name, sortable: true },
    { name: 'Employee Code', selector: (row) => row.emp_code, sortable: true },
    { name: 'Batch No', selector: (row) => row.batch_no || '-', sortable: true, width: '140px' },
    { name: 'Quantity', selector: (row) => row.qty, sortable: true },
    { name: 'Weight', selector: (row) => row.weight, sortable: true },
    { name: 'Return Reason', selector: (row) => row.return_reason || '-', sortable: true },
    { name: 'Created At', selector: (row) => new Date(row.created_at).toLocaleString(), sortable: true },
    { name: 'Status', cell: (row) => (
      <span style={{
        display: 'inline-block',
        border: '1px solid #1976d2',
        color: '#1976d2',
        borderRadius: '16px',
        padding: '2px 12px',
        fontSize: '0.875rem',
        fontWeight: 500,
        background: row.status === 'processed' ? '#e8f5e9' : '#fff3e0',
        minWidth: 32,
        textAlign: 'center',
      }}>{row.status}</span>
    ), ignoreRowClick: true },
    { name: 'Processed By', selector: (row) => row.processed_by || '-', sortable: true },
    { name: 'Processed At', selector: (row) => row.processed_at ? new Date(row.processed_at).toLocaleString() : '-', sortable: true },
    ...(user?.role === 'Admin' ? [{
      name: 'Actions',
      cell: (row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {row.status === 'returned' && (
            <IconButton size="small" color="primary" onClick={() => handleEdit(row.id)}>
              <EditIcon />
            </IconButton>
          )}
          <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
      ignoreRowClick: true,
      allowOverflow: true
    }] : []),
  ];

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        Stock Return List
      </Typography>
      
      {/* Compact Filters Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              fullWidth
              placeholder="Search stock code, name, employee..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="returned">
                  Returned ({statusCounts.returned})
                </MenuItem>
                <MenuItem value="processed">
                  Processed ({statusCounts.processed})
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              onClick={handleOpenFilterModal}
              size="small"
              startIcon={<FilterListIcon />}
              fullWidth
            >
              Filters
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              onClick={handleExport}
              size="small"
              startIcon={<DownloadIcon />}
              fullWidth
            >
              Export
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleClearFilters}
              size="small"
              startIcon={<ClearIcon />}
              fullWidth
            >
              Clear
            </Button>
          </Grid>
        </Grid>
        
        {/* Active Filters Chips */}
        {(search || fromDate || toDate || statusFilter !== 'all' || selectedEmployee || stockCodeFilter) && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {search && (
              <span style={{
                display: 'inline-block',
                border: '1px solid #1976d2',
                color: '#1976d2',
                borderRadius: '16px',
                padding: '2px 12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                background: '#e3f2fd',
                minWidth: 32,
                textAlign: 'center',
                marginRight: 4
              }}>{`Search: "${search}"`}</span>
            )}
            {fromDate && (
              <span style={{
                display: 'inline-block',
                border: '1px solid #1976d2',
                color: '#1976d2',
                borderRadius: '16px',
                padding: '2px 12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                background: '#e3f2fd',
                minWidth: 32,
                textAlign: 'center',
                marginRight: 4
              }}>{`From: ${fromDate}`}</span>
            )}
            {toDate && (
              <span style={{
                display: 'inline-block',
                border: '1px solid #1976d2',
                color: '#1976d2',
                borderRadius: '16px',
                padding: '2px 12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                background: '#e3f2fd',
                minWidth: 32,
                textAlign: 'center',
                marginRight: 4
              }}>{`To: ${toDate}`}</span>
            )}
            {statusFilter !== 'all' && (
              <span style={{
                display: 'inline-block',
                border: '1px solid #1976d2',
                color: '#1976d2',
                borderRadius: '16px',
                padding: '2px 12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                background: '#e3f2fd',
                minWidth: 32,
                textAlign: 'center',
                marginRight: 4
              }}>{`Status: ${statusFilter}`}</span>
            )}
            {selectedEmployee && (
              <span style={{
                display: 'inline-block',
                border: '1px solid #1976d2',
                color: '#1976d2',
                borderRadius: '16px',
                padding: '2px 12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                background: '#e3f2fd',
                minWidth: 32,
                textAlign: 'center',
                marginRight: 4
              }}>{`Employee: ${selectedEmployee.emp_code}`}</span>
            )}
            {stockCodeFilter && (
              <span style={{
                display: 'inline-block',
                border: '1px solid #1976d2',
                color: '#1976d2',
                borderRadius: '16px',
                padding: '2px 12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                background: '#e3f2fd',
                minWidth: 32,
                textAlign: 'center',
                marginRight: 4
              }}>{`Stock Code: "${stockCodeFilter}"`}</span>
            )}
          </Box>
        )}
      </Paper>

      {/* Filter Modal */}
      <Modal
        open={filterModalOpen}
        onClose={handleCloseFilterModal}
        aria-labelledby="filter-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="filter-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
            Advanced Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="From Date"
                type="date"
                value={tempFilters.fromDate || ''}
                onChange={(e) => setTempFilters({...tempFilters, fromDate: e.target.value})}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="To Date"
                type="date"
                value={tempFilters.toDate || ''}
                onChange={(e) => setTempFilters({...tempFilters, toDate: e.target.value})}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Employee</InputLabel>
                <Select
                  value={tempFilters.selectedEmployee?.id || ''}
                  label="Employee"
                  onChange={(e) => {
                    const employee = employees.find(emp => emp.id === e.target.value);
                    setTempFilters({...tempFilters, selectedEmployee: employee || null});
                  }}
                >
                  <MenuItem value="">All Employees</MenuItem>
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.emp_code} - {employee.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Stock Code"
                value={tempFilters.stockCodeFilter || ''}
                onChange={(e) => setTempFilters({...tempFilters, stockCodeFilter: e.target.value})}
                fullWidth
                size="small"
                placeholder="Filter by stock code..."
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={handleCloseFilterModal}>Cancel</Button>
            <Button onClick={handleApplyFilters} variant="contained">Apply</Button>
          </Box>
        </Box>
      </Modal>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onClose={handleEditCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Process Stock Return</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Processed By"
                value={editFormData.processed_by}
                onChange={(e) => setEditFormData({...editFormData, processed_by: e.target.value})}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={editFormData.status}
                  label="Status"
                  onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                >
                  <MenuItem value="returned">Returned</MenuItem>
                  <MenuItem value="processed">Processed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading data: {error}
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={filteredData}
        pagination
        highlightOnHover
        dense
        responsive
        noHeader
        progressPending={loading}
        progressComponent={<CircularProgress />}
      />
    </Box>
  );
};

export default StockReturnTable; 